/* @img-migrated */
import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Calendar,
  User,
  Tag,
  Share2,
  ArrowRight,
  Newspaper,
  Clock,
} from 'lucide-react';
import { toast } from 'sonner';

import Img from '../components/Img';
import { resolveImg } from '../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLACEHOLDER = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200&h=675&fit=crop&auto=format&q=80';

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return iso.slice(0, 10);
  }
};

const estimateReadingTime = (text = '') => {
  const words = (text || '').split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 220));
};

const stripHtml = (html = '') => html.replace(/<[^>]+>/g, '');

/** Body supports either plain text (with \n\n paragraphs) or simple HTML (basic sanitization
 *  is handled upstream — admin content is trusted). */
const BodyRenderer = ({ body = '' }) => {
  if (!body) return null;
  const looksLikeHtml = /<(p|br|img|h\d|ul|ol|li|strong|em|a|blockquote|div)[\s>]/i.test(body);
  if (looksLikeHtml) {
    return (
      <div
        className="prose-like"
         
        dangerouslySetInnerHTML={{ __html: body }}
      />
    );
  }
  return (
    <>
      {body.split(/\n\n+/).map((para, i) => (
        <p key={i} className="mb-5 text-[16px] sm:text-[17px] leading-[1.75] text-slate-700">
          {para.split('\n').map((line, j, arr) => (
            <React.Fragment key={j}>
              {line}
              {j < arr.length - 1 && <br />}
            </React.Fragment>
          ))}
        </p>
      ))}
    </>
  );
};

const BlogDetailPage = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const [postRes, relRes] = await Promise.all([
          axios.get(`${API}/blog/${postId}`),
          axios.get(`${API}/blog/${postId}/related?limit=3`).catch(() => ({ data: [] })),
        ]);
        setPost(postRes.data);
        setRelated(relRes.data || []);
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
        else toast.error('Failed to load this article.');
      } finally {
        setLoading(false);
      }
    })();
  }, [postId]);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: post?.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied to clipboard');
      }
    } catch { /* user cancelled */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 u-page" data-testid="blog-detail-loading">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
          <div className="h-4 w-24 u-skeleton mb-6" />
          <div className="h-9 w-full u-skeleton mb-3" />
          <div className="h-9 w-3/4 u-skeleton mb-8" />
          <div className="aspect-[16/9] w-full u-skeleton rounded-xl mb-8" />
          <div className="space-y-3">
            <div className="h-4 w-full u-skeleton" />
            <div className="h-4 w-full u-skeleton" />
            <div className="h-4 w-5/6 u-skeleton" />
            <div className="h-4 w-3/4 u-skeleton" />
          </div>
        </div>
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-5">
        <div className="text-center" data-testid="blog-detail-not-found">
          <Newspaper className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-[#002249]">Article not found</h1>
          <p className="text-slate-500 mt-2 mb-6">It may have been unpublished or removed.</p>
          <Link to="/news" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#E63946] hover:underline">
            <ArrowLeft className="w-4 h-4" /> Back to News &amp; Insights
          </Link>
        </div>
      </div>
    );
  }

  const image = post.image_url || post.featured_image || PLACEHOLDER;
  const readMins = estimateReadingTime(stripHtml(post.content || post.body || ''));

  return (
    <div className="min-h-screen bg-white u-page" data-testid="blog-detail-page">
      {/* Sticky back bar */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-3 flex items-center justify-between">
          <button
            onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/news'))}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-600 hover:text-[#E63946] u-btn"
            data-testid="blog-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <button
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-600 hover:text-[#002249] u-btn"
            data-testid="blog-share-btn"
          >
            <Share2 className="w-4 h-4" /> Share
          </button>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-8 pb-14">
        {/* Meta row */}
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="flex items-center flex-wrap gap-2 mb-3">
          {post.category && (
            <span className="inline-flex items-center gap-1 text-[10.5px] font-bold tracking-wider text-[#E63946] bg-[#E63946]/8 px-2 py-0.5 rounded-full uppercase" data-testid="blog-category">
              <Tag className="w-3 h-3" />{post.category}
            </span>
          )}
          {post.is_featured && (
            <span className="text-[10.5px] font-bold tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full uppercase">Featured</span>
          )}
          {!post.is_published && (
            <span className="text-[10.5px] font-bold tracking-wider text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full uppercase">Draft preview</span>
          )}
        </motion.div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
          className="text-3xl sm:text-4xl lg:text-[42px] font-bold text-[#0D1B2A] leading-[1.15] tracking-tight"
          data-testid="blog-title"
        >
          {post.title}
        </motion.h1>

        {/* Excerpt */}
        {post.excerpt && (
          <motion.p
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="mt-4 text-[17px] sm:text-[18px] text-slate-500 leading-relaxed"
            data-testid="blog-excerpt"
          >
            {post.excerpt}
          </motion.p>
        )}

        {/* Author + date */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
          className="mt-6 pb-6 border-b border-slate-100 flex items-center flex-wrap gap-4 text-[13px] text-slate-500"
        >
          <span className="inline-flex items-center gap-1.5" data-testid="blog-author">
            <User className="w-3.5 h-3.5" /> {post.author_name || 'DCCF Team'}
          </span>
          <span className="inline-flex items-center gap-1.5" data-testid="blog-date">
            <Calendar className="w-3.5 h-3.5" />
            {formatDate(post.published_at || post.created_at)}
          </span>
          <span className="inline-flex items-center gap-1.5 text-slate-400">
            <Clock className="w-3.5 h-3.5" /> {readMins} min read
          </span>
        </motion.div>

        {/* Hero image */}
        <motion.div
          initial={{ opacity: 0, scale: 0.99 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }}
          className="mt-6 sm:mt-8 rounded-2xl overflow-hidden aspect-[16/9] bg-slate-100"
          data-testid="blog-hero-image"
        >
          <Img variant="generic"
            src={resolveImg(image)}
            alt={post.title}
            onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER; }}
            className="w-full h-full object-cover"
            style={{ objectPosition: `center ${post.image_position || 'center'}` }}
          />
        </motion.div>

        {/* Body */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
          className="mt-8 sm:mt-10 text-slate-700"
          data-testid="blog-body"
        >
          <BodyRenderer body={post.content || post.body || ''} />
        </motion.div>

        {/* Gallery (if present) */}
        {Array.isArray(post.gallery_images) && post.gallery_images.length > 0 && (
          <div className="mt-10" data-testid="blog-gallery">
            <h3 className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-3">Gallery</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {post.gallery_images.map((g, i) => (
                <Img variant="generic"
                  key={i}
                  src={g}
                  alt={`Gallery ${i + 1}`}
                  className="w-full aspect-square object-cover rounded-lg"
                  loading="lazy"
                />
              ))}
            </div>
          </div>
        )}

        {/* Share footer */}
        <div className="mt-12 pt-6 border-t border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <div className="text-[12px] text-slate-400">
            Published {formatDate(post.published_at || post.created_at)} · by {post.author_name || 'DCCF Team'}
          </div>
          <button
            onClick={handleShare}
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#002249] hover:text-[#E63946] u-btn"
          >
            <Share2 className="w-4 h-4" /> Share this article
          </button>
        </div>
      </article>

      {/* Related posts */}
      {related.length > 0 && (
        <section className="bg-slate-50 border-t border-slate-100 py-12" data-testid="blog-related">
          <div className="max-w-5xl mx-auto px-5 sm:px-8">
            <h2 className="text-[18px] sm:text-[20px] font-bold text-[#002249] mb-5 flex items-center gap-2">
              <Newspaper className="w-4.5 h-4.5" /> You might also like
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {related.map((r) => {
                const rImg = r.image_url || r.featured_image || PLACEHOLDER;
                return (
                  <Link
                    key={r.id}
                    to={`/news/${r.id}`}
                    className="u-card group bg-white rounded-xl overflow-hidden border border-slate-100 hover:border-[#002249]/30 shadow-sm"
                    data-testid={`related-post-${r.id}`}
                  >
                    <div className="aspect-[16/9] overflow-hidden bg-slate-100">
                      <Img variant="generic"
                        src={resolveImg(rImg)}
                        alt={r.title}
                        onError={(e) => { if (e.currentTarget.src !== PLACEHOLDER) e.currentTarget.src = PLACEHOLDER; }}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      {r.category && (
                        <span className="inline-block text-[9.5px] font-bold tracking-wider text-[#E63946] bg-[#E63946]/8 px-1.5 py-0.5 rounded uppercase mb-2">{r.category}</span>
                      )}
                      <h3 className="text-[14.5px] font-bold text-[#0D1B2A] leading-snug line-clamp-2 group-hover:text-[#E63946] transition-colors">{r.title}</h3>
                      <p className="text-[12px] text-slate-500 mt-1.5 line-clamp-2">{r.excerpt || stripHtml(r.content || '').slice(0, 120)}</p>
                      <div className="mt-3 text-[11.5px] font-semibold text-[#002249] inline-flex items-center">
                        Read article <ArrowRight className="w-3 h-3 ml-1 group-hover:translate-x-0.5 transition-transform" />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogDetailPage;
